import { type ReactNode, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import classnames from "classnames";

import { Card } from "common/core/card";
import { Heading, Substyle } from "common/core/typography";
import { Badge } from "common/core/badge";
import IdentityTimeline from "common/identity/timeline";
import { getProofRequirementTexts } from "common/details/identity/signer/profile";
import { IndividualCredentialResult } from "graphql_globals";

import type { SidebarCustomerSigner as CustomerSigner } from "./index.fragment.graphql";
import Styles from "./index.module.scss";

type Props = {
  customerSigner: CustomerSigner;
  showSignals: boolean;
};

function PhotoIDResultBadge({
  result,
  title,
}: {
  result: IndividualCredentialResult;
  title: ReactNode;
}) {
  if (result === IndividualCredentialResult.PASSED) {
    return (
      <Badge withIcon="check-circle" kind="success">
        <FormattedMessage
          id="bb8d4e7d-a3f1-4ca9-955f-a8921bb8b89b"
          defaultMessage="{title} passed"
          values={{ title }}
        />
      </Badge>
    );
  }

  if (result === IndividualCredentialResult.FAILED) {
    return (
      <Badge withIcon="x-mark-circle" kind="danger">
        <FormattedMessage
          id="21745dff-dbaf-497d-9f43-1fe4a611a1ad"
          defaultMessage="{title} failed"
          values={{ title }}
        />
      </Badge>
    );
  }

  return null;
}

function Profile({ customerSigner }: { customerSigner: CustomerSigner }) {
  const mostRecentSignerIdentity = customerSigner.signerIdentities?.[0];
  const proofRequirementTexts = getProofRequirementTexts(customerSigner.proofRequirement);

  const photoId = mostRecentSignerIdentity?.photoId;
  const hasRequirements = Boolean(proofRequirementTexts.length);

  return (
    <div className={classnames(Styles.profile, !hasRequirements && Styles.noRequirements)}>
      {photoId && (
        <div>
          <PhotoIDResultBadge
            result={photoId.credentialResult}
            title={
              <FormattedMessage
                id="3d064405-5633-4c64-b9c8-965485ddde6c"
                defaultMessage="ID Verification"
              />
            }
          />
          <PhotoIDResultBadge
            result={photoId.selfieResult}
            title={
              <FormattedMessage id="8ca1b124-35cb-4e66-bc19-c8e6d0600066" defaultMessage="Selfie" />
            }
          />
        </div>
      )}

      {hasRequirements && (
        <>
          <Heading textStyle="headingSix" level="h4">
            <Substyle size="defaultSize">
              <FormattedMessage
                id="64f1f7ea-2957-4130-88e0-34d137e13178"
                defaultMessage="Verification methods"
              />
            </Substyle>
          </Heading>
          {proofRequirementTexts.map((requirement, i) => (
            <span key={`requirement-${i}`}>{requirement}</span>
          ))}
        </>
      )}
    </div>
  );
}

export function Sidebar({ customerSigner, showSignals }: Props) {
  // The timeline here is an array of `identityTimeline` - one for each signer identity. We need to do two things on the client:
  // 1. Reverse the order of the timeline for each signer identity
  // 2. Remove the "Proof account created" and "Email verified" events from all but the last signer identity (the first attempt)
  const timeline = useMemo(
    () =>
      customerSigner.signerIdentities?.map((si, i, arr) => {
        const timeline =
          i === arr.length - 1
            ? si.identityTimeline
            : si.identityTimeline.filter(
                (el) => el.title !== "Proof account created" && el.title !== "Email verified",
              );

        return [...timeline].reverse();
      }) || [],
    [customerSigner.signerIdentities],
  );
  return (
    <Card fullWidth noMargin className={Styles.container} automationId="identity-timeline">
      <Profile customerSigner={customerSigner} />
      <IdentityTimeline identityTimeline={timeline} showHeading={false} showSignals={showSignals} />
    </Card>
  );
}
